// Канонический setError — глобальный mixin (main.js: Vue.mixin), замена 108
// копипастных копий в компонентах. Поведение — супермножество вариантов:
// полные гварды на разлогин (Signin), сброс зависшего isLoading (PageTemplate,
// Signin), this.error для страниц, что его рендерят (лишним не бывает).
// Транзиентные сетевые ошибки НЕ показывают красный тост — их индицирует
// глобальный баннер «Проблема с сетью» в App.vue (см. createNetworkFlagInterceptor).
import { isTransientNetworkError, isLongOperationTimeout, rateLimitedMessage } from "@/utils/networkErrors";
import { isSessionDeadError } from "@/utils/sessionExpiry";
import { isMoneyDeclined } from "@/utils/moneyConfirmGuard";
import { configureRollbarPerson } from "@/utils/rollbarPerson";

// Ожидаемый 422 (валидация) — не баг: в Rollbar не шлём (шум, тот же класс
// гигиены, что network-noise), пользователю показываем ПРИЧИНУ сервера, а
// не генерик. Инцидент 2026-07-22: «Ошибка при добавлении бронирования!»
// скрывала «у машины нет почасовой цены» (no_price) — пользователь не
// понимал, что чинить, а Rollbar копил не-баги. Модульная функция (не
// метод): setError зовут и с фейковыми this в спеках компонентов.
// Легаси-строки бизнес-отказов сервера (406 not_acceptable) → i18n-ключ.
// Issue #127: «Ошибка удаления архивных машин» скрывала «Can't destroy,
// has bookings» — осознанный отказ, а не сбой.
const BUSINESS_REFUSAL_KEYS = {
  "Can't destroy, has bookings": "car_has_bookings",
  "Can't deactivate, has active bookings": "car_has_active_bookings",
  "Cars limit reached": "cars_limit_reached",
  "Trial period ended": "trial_ended",
  "periods in branches must be equal": "periods_must_be_equal",
  // Денежный контур (422, api#743 / api#749): правка проведённого платежа —
  // тип, доход/расход, касса, владелец денег — и платёж без одного типа оплаты.
  "Posted count payment details cannot be changed": "posted_count_details_locked",
  "Unsupported payment type": "unsupported_payment_type",
  // Инвариант модели «Отъездила» ⇒ ride (api Booking::RIDED_STATE_REQUIRES_RETURN): путь без операции
  // (создание, прямой вызов) приходит строкой RecordInvalid, а не кодом booking_not_returned.
  "Only a returned booking can be set to «Отъездила»; accept the car back first": "booking_not_returned",
  // Бронирование действующего договора выкупа (api RentToOwn::ManagedByContract): тело — голая строка-код
  // { error: "managed_by_contract" }. Карточка разбирает его parseRentToOwnError; списки, доска, календарь,
  // агенты и карточка машины — здесь, иначе тост показывал сырой код.
  "managed_by_contract": "managed_by_contract",
  // FT-193: источник «баланс и карта» при неработающем терминале Т-Банка — 422 { periodic_funding: ["tinkoff_unavailable"] }
  // на создании и правке бронирования; без разбора тост показывал бы сырой код.
  "tinkoff_unavailable": "tinkoff_unavailable",
  // FT-193: списание с карты у клиента без привязанной карты — 406, тело — голая строка (Operations::Money::BookingPayment).
  "Client not have rebill_id": "tinkoff_card_missing",
};

// 404 обрабатывается ИЗБИРАТЕЛЬНО, в отличие от 422/406.
//
// Целиком «ожидаемым» его делать нельзя: 404 на несуществующем маршруте или на
// сущности, которая обязана была быть, — настоящий баг, и он должен и дальше
// попадать в Rollbar красным. Поэтому ожидаемым считается только 404, у
// которого сервер вернул УЗНАННУЮ строку отказа. Всё остальное падает в общую
// ветку без изменений.
//
// Причина появления (2026-08-04): машину переносят в другой филиал
// (PATCH /api/v1/cars/:id со сменой company_id), а у сотрудника старого
// филиала остаётся открытая карточка. Вкладка «Обслуживание» дёргает
// /api/v1/car_maintenances/:car_id, тот честно отвечает 404 — но сотрудник
// видел общий «Ошибка», а Rollbar собирал ~230 таких не-багов в сутки.
const NOT_FOUND_REFUSAL_KEYS = {
  "Car not found in your branch": "car_in_other_branch",
};

// Единый разбор кода отказа. Сервер отвечает тремя формами тела, и прежние
// он держит ради старых бандлов мобилки:
//   { error: { code, message } } — конверт новых отказов операций (FT-184);
//   { error: "текст", code }     — прежние тела (OperationResponder ERROR_WITH_CODE:
//                                  booking_already_returned, staff_only, ...);
//   { error: "строка" }          — код здесь НЕ угадывается: «Car not found» от
//                                  «managed_by_contract» без белого списка не
//                                  отличить — это работа модульных разборщиков
//                                  со своими списками (parseRentToOwnError).
// Плоский код — только строкой поля `code` (error_code FT-150 — другой контракт).
// Пространство errors.codes.* общее для обеих форм: общий ключ вроде
// validation_failed / not_found / conflict / rate_limited перекроет плоские
// коды CRM и прочих экранов, у которых сервер шлёт уже переведённый и более
// точный текст (rate_limited — с секундами). Такие имена сюда не заводить.
export function extractValidationDetail(error) {
  const data = error && error.response && error.response.data;
  if (!data) return { code: null, detail: null };
  // Голая строка в теле — только узнанный отказ из белого списка: HTML и прочий текст 5xx в detail не попадает.
  if (typeof data === "string") {
    const bare = data.trim();
    return { code: null, detail: Object.prototype.hasOwnProperty.call(BUSINESS_REFUSAL_KEYS, bare) ? bare : null };
  }
  const err = data.error;
  if (err && typeof err === "object") {
    return { code: err.code || null, detail: err.message || null };
  }
  if (typeof err === "string") return { code: typeof data.code === "string" && data.code ? data.code : null, detail: err };
  if (typeof data === "object") {
    // render json: booking.errors — hash {поле: ["сообщение", ...]}
    const msgs = Object.values(data).flat().filter((v) => typeof v === "string");
    if (msgs.length) return { code: null, detail: msgs.join("; ") };
  }
  return { code: null, detail: null };
}

export default {
  methods: {
    // options.silent — «человеку уже сказано»: отказ объясняет блок на самой
    // странице (LoadFailedRetry), и красный тост поверх него был бы вторым
    // сообщением об одной ошибке. Репорт в Rollbar при этом остаётся.
    setError(error, text, options) {
      const silent = !!(options && options.silent);
      // FT-136: 401 класса «сессия мертва» уже объяснён источником — интерсептор
      // показал единственный тост и оставил нотис форме входа ДО перехода. Второй
      // тост не добавится, а затрёт первый: снекбар в приложении один
      // (notifications.js:63-67 + App.vue:248-256). Rollbar тоже не зовём: истёкший
      // JWT — ожидаемое состояние, как 422 и 406 ниже, а не баг.
      if (isSessionDeadError(error)) {
        if (this.isLoading === true) {
          this.isLoading = false;
        }
        return;
      }

      // Оператор сам отказался отправлять необычно крупную сумму (moneyConfirmGuard).
      // Запрос отменён ДО отправки, сбоя не было. Тот же класс, что и ветка выше:
      // тост тут показал бы «ошибку» на осознанное действие человека, а Rollbar
      // копил бы не-баги. Единственное, что нужно, — снять зависший isLoading:
      // форма ушла в отправку и без этого осталась бы заблокированной.
      if (isMoneyDeclined(error)) {
        if (this.isLoading === true) {
          this.isLoading = false;
        }
        return;
      }

      const status = error && error.response && error.response.status;
      const { code, detail } = extractValidationDetail(error);
      // 422 = валидация, 406 = бизнес-отказ (легаси cars-контроллер) — оба
      // ожидаемые целиком: warning с причиной, Rollbar не зовём.
      // 404 — только с узнанной строкой отказа (см. NOT_FOUND_REFUSAL_KEYS).
      const notFoundSlug = status === 404 && detail ? NOT_FOUND_REFUSAL_KEYS[detail] : null;
      // Считается один раз на оба пути: сервер шлёт бизнес-отказы как
      // { error: { code, message } } с английским message, и переводить их по
      // коду надо независимо от того, ожидаемый это статус или нет. Иначе на
      // 400/402/404/429 пользователь увидит английский текст сервера.
      const codeKey = code && this.$te(`errors.codes.${code}`) ? `errors.codes.${code}` : null;
      // Ожидаемые отказы считают только свой текст; тост, сброс isLoading и
      // this.error у них общие.
      let human = null;
      if (status === 422 || status === 406 || notFoundSlug) {
        const refusalSlug = notFoundSlug || (detail && BUSINESS_REFUSAL_KEYS[detail]);
        const refusalKey = refusalSlug && this.$te(`errors.refusals.${refusalSlug}`) ? `errors.refusals.${refusalSlug}` : null;
        human = (codeKey && this.$t(codeKey)) || (refusalKey && this.$t(refusalKey)) || detail || text;
      } else if (status === 429) {
        // Лимит частоты запросов (см. isRateLimitedError): подсказать, сколько
        // ждать. Переведённый серверный код объясняет точнее общего текста.
        human = (codeKey && this.$t(codeKey)) || rateLimitedMessage(this, error);
      } else if (silent && status === 409 && code === "locked") {
        // 409 locked при silent: первый запрос с тем же ключом идемпотентности ещё выполняется — вызывающий
        // (повтор списания с карты, BookingCard.savePayment) сам объяснил это человеку и повторит с тем же
        // ключом. Это ожидаемое состояние, а не сбой: в Rollbar не репортим. Остальные 409 и все 5xx идут ниже.
        human = (codeKey && this.$t(codeKey)) || detail || text;
      }
      if (human !== null) {
        if (!silent) this.$notify(text, human, "warning", 6000);
        console.log(error);
        if (this.isLoading === true) {
          this.isLoading = false;
        }
        this.error = human;
        return;
      }

      configureRollbarPerson(this);
      this.$reportError(text, error);
      if (isLongOperationTimeout(error)) {
        // Долгая операция не уложилась в СВОЙ поднятый таймаут. Глобальный
        // баннер «Проблема с сетью» этот случай намеренно пропускает
        // (networkErrors.js), поэтому объяснить обязано это место — иначе
        // экран промолчал бы вовсе. Сырой `${error}` тут не нужен: строка
        // «timeout of 75000ms exceeded» пользователю ничего не говорит.
        if (!silent) this.$notify(text, this.$t("errors.operation_timeout"), "warning", 6000);
      } else if (!isTransientNetworkError(error) && !silent) {
        this.$notify(this.$t("errors.error"), `${error}. ${text}`, "error", 5000);
      }
      console.log(error);
      if (this.isLoading === true) {
        this.isLoading = false;
      }
      // detail — уже нормализованная строка (extractValidationDetail достаёт
      // message из объектной формы). Раньше здесь лежал сырой data.error, и
      // на { error: { code, message } } в this.error попадал ОБЪЕКТ — шаблоны,
      // которые его рендерят, показывали "[object Object]".
      this.error = (codeKey && this.$t(codeKey)) || detail || text;
    },
  },
};
